/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type OnInit, type SimpleChanges, Component, Injector, Input } from '@angular/core';
import { type ColumnDef, type Rest, type TableConfig, ButtonService, TableAbstract, UtilService } from '@dm/core';
import { type Button } from '@dm/core/types';
import { FileSystemModeE } from 'app/common/constants';
import { type CIFSClient, type CIFSShare, type FileSystem } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import { finalize } from 'rxjs/operators';

import { FileSystemEntryE } from '../../../file-system/create-file-system/create-file-system.component';
import { CifsShareStrService } from '../cifs-share-str.service';
import { ShareTypeE } from './../../share.enum';
import { AddUserOrGroupComponent } from './add-user-or-group/add-user-or-group.component';
import { ClientCifsShareService } from './client-cifs-share.service';
import { prefixHandler } from './client-clifs-share';
import { ModifyClientCifsShareComponent } from './modify-client-cifs-share/modify-client-cifs-share.component';
import { RemoveClientCifsShareComponent } from './remove-client-cifs-share/remove-client-cifs-share.component';

@Component({
    selector: 'app-client-cifs-share',
    templateUrl: './client-cifs-share.component.html',
    styleUrls: ['./client-cifs-share.component.scss'],
})
export class ClientCifsShareComponent extends TableAbstract<CIFSClient> implements OnInit, OnChanges {
    @Input() cifsShare: CIFSShare;
    @Input() entry: FileSystemEntryE;
    /**
     * 传入的文件系统
     */
    @Input() fileSystem: FileSystem;

    i18n = this.str.i18n;

    tableRest: Rest;
    tableCountRest: Rest;
    tableConfig: TableConfig;
    tableColumnDefs: ColumnDef[];
    selection: any[] = [];
    loading = false;
    isSimpleMode = SWITCH.IS_SIMPLE_MODE;
    /**
     * 查询到的关联文件系统
     */
    gotFileSystem: FileSystem;

    addBtn: Button;
    modifyBtn: Button;
    removeBtn: Button;

    constructor(
        public str: CifsShareStrService,
        private service: ClientCifsShareService,
        private buttonService: ButtonService,
        private utilService: UtilService,
        injector: Injector,
    ) {
        super(injector);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((!changes.cifsShare.previousValue && changes.cifsShare.currentValue) ||
            (changes.cifsShare.previousValue && changes.cifsShare.currentValue &&
                changes.cifsShare.currentValue.ID !== changes.cifsShare.previousValue.ID)) {
            this.tableRest = this.service.getBatchRest(changes.cifsShare.currentValue.ID, this.cifsShare.vstoreId);
            this.tableCountRest = this.service.countRest(changes.cifsShare.currentValue.ID, this.cifsShare.vstoreId);
            // 审计日志文件系统创共享要限制权限，先过滤是不是homedir共享
            // 支持根目录的需求DM暂时未做，这里先规避没有文件系统的情况
            if (this.cifsShare.subType !== ShareTypeE.Homedir && this.cifsShare.FSID !== '--') {
                this.loading = true;
                this.service.getFileSystem(this.cifsShare.FSID, this.cifsShare.vstoreId)
                    .pipe(
                        finalize(() => {
                            this.loading = false;
                        }),
                    )
                    .subscribe((data) => {
                        if (!data) {
                            return;
                        }
                        if (data.isAuditLogFs || data.SUBTYPE === FileSystemModeE.SMART_MOBILITY_INNER_FILE_SYSTEM ||
                            data.SUBTYPE === FileSystemModeE.HASH) {
                            this.entry = FileSystemEntryE.AuditLog;
                        } else {
                            this.entry = undefined;
                        }

                        this.gotFileSystem = data;
                    });
            }
        }
    }

    initBtns(): void {
        this.addBtn = this.buttonService.create({
            id: 'add-client-cifs-share',
            label: this.i18n.btn.addBtn,
            theme: 'primary',
            permission: ['share_W'],
            depPermission: ['task_W'],
            command: () => {
                this.add();
            },
        });
        this.modifyBtn = this.buttonService.create({
            id: 'modify-client-cifs-share',
            label: this.i18n.btn.modifyBtn,
            permission: ['share_W'],
            command: ({ data }) => {
                this.modify(data);
            },
        });
        this.removeBtn = this.buttonService.create({
            id: 'remove-client-cifs-share',
            label: this.i18n.btn.removeBtn,
            permission: ['share_W'],
            disabledCheck: (data) => !data && this.selection.length === 0,
            command: ({ data }) => {
                this.remove(data ? [data] : this.selection);
            },
        });
    }

    initTable(): void {
        this.tableConfig = {
            dataKey: 'ID',
            selectionMode: 'checkbox',
            pollingPeriod: 15,
            menuItems: [
                this.modifyBtn,
                this.removeBtn,
            ],
        };

        this.tableColumnDefs = [
            {
                field: 'NAME',
                header: this.i18n.label.userOrGroup,
                filterType: 'search',
                render: (value: string) => prefixHandler(value), // 如果是用户组，会多一个@符号
                filterTrim: false,
            },
            {
                field: 'ID',
                header: this.i18n.label.id,
                hidden: true,
                columnFilter: true,
            },
            {
                field: 'DOMAINTYPE',
                header: this.i18n.label.type,
                columnFilter: true,
                render: (value, col, data: CIFSClient) => this.str.getUpperCaseUserOrGroupType(data),
            },
            {
                field: 'PERMISSION',
                header: this.i18n.label.permissionLevel,
                columnFilter: true,
                filterType: 'dropdown',
                options: this.str.permissionLevelOpts,
                render: (value) => this.utilService.getLabelFromOptions(this.str.permissionLevelOpts, Number(value)),
            },
        ];
    }

    add(): void {
        const selectedVstore = this.layout
            ? this.layout.selectedVstore.label
            : { label: this.cifsShare.vstoreName, value: this.cifsShare.vstoreId };
        this.doAction(AddUserOrGroupComponent, {
            cifsShare: this.cifsShare,
            entry: this.entry,
            fileSystem: this.fileSystem || this.gotFileSystem,
            selectedVstore,
        });
    }

    modify(data: CIFSClient): void {
        this.doAction(ModifyClientCifsShareComponent, {
            data,
            entry: this.entry,
            cifsShare: this.cifsShare,
            fileSystem: this.fileSystem || this.gotFileSystem,
        });
    }

    remove(dataList: CIFSClient[]): void {
        this.doAction(RemoveClientCifsShareComponent, { dataList, cifsShare: this.cifsShare });
    }
}
